import { KEEPSAKE_SKILL } from '~/data/keepsakes'
import { TUNING } from '~/config/tuning'
import { CREATURE_POOL, getCreatureDef, rollRecruitOptions } from '~/data/creatures'
import { TRAINED_SKILL_IDS } from '~/data/trainedSkills'
import type { Creature, RewardPool, RewardResolution, RunState } from '~/types/game'
import { ELEMENTS } from '~/types/game'
import { canLearnSkill, grantXp, greenThumbHeal, rootstockRestBoost, healInjuriesFully, injuryStacks, rollRecruitStats } from '~/utils/battle'
import { activeWeeklyMod, hasWeeklyMod } from '~/data/weeklyMods'
import { shuffle, weightedPick } from '~/utils/random'
import { drawThroughCycle } from '~/utils/offerCycle'
import { ownedSynergyIds, synergyWeightMult } from '~/data/synergies'
import { hasStarweave } from '~/utils/starweaveRun'

/**
 * Structured reward config (design notes: keep reward logic out of UI).
 * `apply` mutates the run and may return a RewardResolution when the
 * reward needs a follow-up pick. Rarity is unassigned until simulations.
 */
export interface RewardDef {
  id: string
  name: string
  icon: string
  /** 🎨 Painted icon (v1.90): shown big on pick cards; emoji fallback. */
  image?: string
  description: string
  /** Run-aware description override (e.g. Full Camp shows the actual
   * HP amount); falls back to `description` where no run exists. */
  describe?: (run: RunState) => string
  pool: RewardPool
  tags: string[]
  weight: number
  simulation: { rarity: null; estimatedPower: null; notes: string }
  /** Only meaningful while the injury system is enabled. */
  needsInjury?: boolean
  canOffer?: (run: RunState) => boolean
  apply: (run: RunState) => RewardResolution | void
}

/** False while the reward's system (e.g. injuries) is disabled.
 * Injuries exist on Ascension 1+ (v0.60); see relicAvailable's warning
 * about bare `.filter(rewardAvailable)`. */
export function rewardAvailable(def: RewardDef, ascension = 0): boolean {
  return !def.needsInjury || TUNING.injury.enabled || ascension >= 1
}

const sim = { rarity: null, estimatedPower: null, notes: 'Rarity to be assigned after simulation.' }

/**
 * 🎓 Trained skills the run has NOT taken yet (v2.67, per Javi: "I chose
 * the +3 surge skill and it appeared again as an option").
 *
 * The old rule kept a skill offerable while any ONE creature still
 * lacked it — so teaching Prism Surge to a single creature left it in
 * the pool for the other three, and it came back around. A skill is now
 * spent for the whole run the moment anybody learns it, which is what
 * "don't offer it twice" means from the player's seat.
 *
 * Both entry points (the Training Ground reward and the shop's standing
 * Training Ground purchase) run through this, so they cannot drift.
 */
export function teachableSkills(run: RunState): string[] {
  // 🎓 v4.16 (per Javi): duplicates are teachable again — a skill only
  // leaves the curriculum once EVERY creature knows it. One creature
  // still never learns the same skill twice, and team-wide effects
  // apply once per team (no stacking — see slotCapacity / martyr).
  // v4.70: canLearnSkill also excludes the Mirricrow/Understudy loop —
  // a skill only ONE blocked creature could "learn" must not be offered.
  // 🎓 v5.86: a run teaches only its frozen pool (Javi's ascension
  // ladder). No pool stored (weekly runs, older saves) = the full set.
  const pool = run.skillPool ? new Set(run.skillPool) : null
  return TRAINED_SKILL_IDS.filter(id =>
    (pool === null || pool.has(id))
    && run.roster.some(c => canLearnSkill(c, id)))
}

/** 🎓 The three offers for EVERY skill-teaching surface — the field
 * school, the Training Ground reward card and the shop special all draw
 * here (v5.17 unified; the reward card used an unweighted sample
 * before): TEACHABLE skills only (v3.19 audit fix), synergy-nudged
 * toward the run's relics and learned skills (v2.38), and riding the
 * decline cycle (v5.17, per Javi) — a passed-over skill waits until the
 * rest of the curriculum has come around. Returns fewer than three
 * (possibly zero) when the curriculum is nearly exhausted. */
export function rollTrainingOptions(run: RunState, settings: { mission?: boolean; exclude?: readonly string[]; preservePinned?: boolean } = {}): string[] {
  // 🎲 v5.56 (per Javi): in a DAILY the curriculum is part of the
  // skeleton — a seed-shuffled deck rolled at run start (useRun). Every
  // surface deals the first TEACHABLE entries in deck order, then
  // rotates the offered ones to the back: a passed-over skill still
  // waits for the curriculum to come around, but deterministically.
  // Synergy nudges and decline bags are personal, so they sit the
  // daily out; two dispatchers with the same roster knowledge see the
  // same three offers.
  if (run.mode === 'daily' && Array.isArray(run.trainingDeck) && run.trainingDeck.length > 0) {
    // 🗓️⚗️ v5.66: One School halves the offer; Fresh Legs pulls the
    // travel skills nobody could use that week.
    const mod = activeWeeklyMod(run)
    const teachable = new Set(teachableSkills(run)
      .filter(id => !mod?.excludeSkills?.includes(id)))
    const options = run.trainingDeck.filter(id => teachable.has(id)).slice(0, mod?.id === 'oneschool' ? 2 : 3)
    if (options.length > 0) {
      run.trainingDeck = [
        ...run.trainingDeck.filter(id => !options.includes(id)),
        ...options,
      ]
    }
    return options
  }
  const owned = ownedSynergyIds(run)
  const draw = (ids: string[], n: number) => {
    const pool = ids.map(id => [id, synergyWeightMult(id, owned)] as [string, number])
    const options: string[] = []
    while (options.length < n && pool.length > 0) {
      const pick = weightedPick(pool)
      pool.splice(pool.findIndex(([id]) => id === pick), 1)
      options.push(pick)
    }
    return options
  }
  // 🎓 v6.85 (per Javi): Broader Curriculum no longer widens the offer - it
  // lets one Training Mission per run teach TWO of the three (see
  // canStarweaveDoubleTraining). Three choices everywhere again.
  const count = 3
  const eligible = teachableSkills(run)
  const fresh = eligible.filter(id => !settings.exclude?.includes(id))
  // A reroll cycles away from this offer when there are alternatives;
  // near exhaustion it still returns every distinct teachable choice.
  const candidates = fresh.length >= Math.min(count, eligible.length) ? fresh : eligible
  const options = drawThroughCycle(run, 'skill', candidates, count, draw)
  // 🎗️ v6.54 (per Javi): the keepsake lesson rides the run's FIRST
  // Training Grounds as a fourth option - once, campaign only.
  // 🎗️ v7.48 (per Javi): never in the gauntlet (a save from before still carries the flags).
  if (!settings.preservePinned && run.mode !== 'daily' && !run.gauntlet && run.keepsakes?.skill && !run.keepsakeOffered?.skill) {
    options.push(KEEPSAKE_SKILL)
    run.keepsakeOffered = { ...run.keepsakeOffered, skill: true }
  }
  return options
}

/** 🩸 v5.66 Week of Scars: cleansing at CAMP hardens the veteran —
 * +1 permanent to the creature's best element per stack cured. Other
 * cures (Bloom, medics) stay pure cures; the camp is the ritual. */
function cureAtCamp(run: RunState, creature: Creature) {
  const stacks = injuryStacks(creature)
  healInjuriesFully(creature)
  if (stacks > 0 && hasWeeklyMod(run, 'scars')) {
    const best = ELEMENTS.reduce((a, b) => (creature.stats[b] > creature.stats[a] ? b : a))
    creature.stats[best] += stacks
  }
}

function heal(run: RunState, amount: number) {
  // 🌿 Green Thumb (v2.87): a gardener on the roster makes every rest
  // stop go further, and at Apotheosis clears the whole ward.
  const thumb = greenThumbHeal(run.roster)
  if (thumb.cureAll) for (const creature of run.roster) cureAtCamp(run, creature)
  // 🌳 Rootstock Apotheosis (v3.07 audit fix): rest stops also grant
  // the whole team +1 permanent to every element.
  rootstockRestBoost(run.roster)
  run.hp = Math.min(run.maxHp, run.hp + amount + thumb.heal)
}

function cleanseInjuries(run: RunState): number {
  let cleansed = 0
  for (const creature of run.roster) {
    if (creature.injured) {
      cureAtCamp(run, creature)
      cleansed++
    }
  }
  return cleansed
}

function anyInjured(run: RunState) {
  return run.roster.some(c => c.injured)
}

/** Fully heals `count` creatures, always the worst-injured first
 * (v5.22, per Javi: a cure clears a WHOLE creature, stacks and all —
 * the Salve's everyone-rule, portioned out). */
function cureCreatures(run: RunState, count: number) {
  for (let i = 0; i < count; i++) {
    const worst = [...run.roster].sort((a, b) => injuryStacks(b) - injuryStacks(a))[0]
    if (!worst || !worst.injured) return
    healInjuriesFully(worst)
  }
}

function lowestLevelCreature(run: RunState) {
  const sorted = shuffle(run.roster).sort((a, b) => a.level - b.level || a.xp - b.xp)
  return sorted[0]
}

/** The always-valid fallback when a pool produces nothing (design notes). */
export const FALLBACK_REWARD_ID = 'group-practice'

export const REWARD_DEFS: RewardDef[] = [
  // ---- REGULAR_REWARD_POOL: regular battles + elite/boss sequence 1 ----
  {
    id: 'group-practice',
    name: 'Group Practice',
    icon: '✨',
    description: 'All creatures gain 2 XP.',
    pool: 'regular',
    tags: ['xp', 'group', 'regular'],
    weight: 100,
    simulation: sim,
    apply: (run) => {
      for (const creature of run.roster) grantXp(creature, 2, run)
    },
  },
  {
    id: 'field-promotion',
    name: 'Field Promotion',
    icon: '🎖️',
    description: 'Every creature gains a full XP bar\'s worth of XP.',
    pool: 'regular',
    tags: ['xp', 'level', 'group', 'regular'],
    weight: 15,
    simulation: sim,
    apply: (run) => {
      // A FULL BAR each (v1.77, per Javi — the old top-up threw away
      // progress already earned): everyone levels, and mid-bar XP
      // carries into the next bar instead of vanishing.
      for (const creature of run.roster) {
        grantXp(creature, TUNING.xp.toNextLevel(creature.level), run)
      }
    },
  },
  {
    id: 'private-lesson',
    name: 'Private Lesson',
    icon: '📖',
    description: 'Choose one creature. It gains 5 XP.',
    pool: 'regular',
    tags: ['xp', 'targeted', 'regular'],
    weight: 100,
    simulation: sim,
    canOffer: run => run.roster.length >= 1,
    apply: () => ({ type: 'pick-creatures', defId: 'private-lesson', count: 1, xpEach: 5, picked: [] }),
  },
  {
    id: 'catch-up-training',
    name: 'Catch-Up Training',
    icon: '🎯',
    description: 'Your lowest-level creature gains 6 XP.',
    pool: 'regular',
    tags: ['xp', 'catchup', 'regular'],
    weight: 80,
    simulation: sim,
    canOffer: run => run.roster.length >= 1,
    apply: (run) => {
      const creature = lowestLevelCreature(run)
      if (creature) grantXp(creature, 6, run)
    },
  },
  {
    id: 'shared-notes',
    name: 'Shared Notes',
    icon: '📝',
    // 6 XP since v2.27 (5 since v0.77): the bench deserves real catch-up.
    description: 'All creatures that did not complete a mission this encounter gain 6 XP.',
    pool: 'regular',
    tags: ['xp', 'bench', 'catchup', 'regular'],
    weight: 70,
    simulation: sim,
    canOffer: run =>
      !!run.encounter && run.roster.some(c => !run.encounter!.succeededIds.includes(c.id)),
    apply: (run) => {
      for (const creature of run.roster) {
        if (!run.encounter?.succeededIds.includes(creature.id)) grantXp(creature, 6, run)
      }
    },
  },
  {
    id: 'battle-review',
    name: 'Battle Review',
    icon: '🔍',
    description: 'All creatures assigned to at least one mission this encounter gain 3 XP.',
    pool: 'regular',
    tags: ['xp', 'active', 'regular'],
    weight: 70,
    simulation: sim,
    canOffer: run =>
      !!run.encounter && run.roster.some(c => run.encounter!.everAssignedIds.includes(c.id)),
    apply: (run) => {
      for (const creature of run.roster) {
        if (run.encounter?.everAssignedIds.includes(creature.id)) grantXp(creature, 3, run)
      }
    },
  },
  {
    id: 'team-drill',
    name: 'Team Drill',
    icon: '🤝',
    description: 'Choose two creatures. Each gains 3 XP.',
    pool: 'regular',
    tags: ['xp', 'targeted', 'regular'],
    weight: 80,
    simulation: sim,
    canOffer: run => run.roster.length >= 1,
    apply: run => ({
      type: 'pick-creatures',
      defId: 'team-drill',
      count: Math.min(2, run.roster.length),
      xpEach: 3,
      picked: [],
    }),
  },
  {
    id: 'new-companion',
    name: 'New Companion',
    icon: '🐾',
    description: 'Increase max roster size by 1. Choose 1 of 3 new creatures to join your team.',
    pool: 'regular',
    tags: ['creature', 'roster', 'rare', 'regular'],
    weight: 15,
    simulation: sim,
    // 🎒 v5.66 Loadout Lock week: no recruits — this gate covers the
    // reward pool AND the shop special (both check canOffer).
    canOffer: run =>
      !hasWeeklyMod(run, 'loadout')
      && run.roster.length < TUNING.rewards.maxRosterSize
      && CREATURE_POOL.some(c => !run.roster.some(r => r.defId === c.id)),
    apply: (run) => {
      run.maxRosterSize++
      const taken = new Set(run.roster.map(c => c.defId))
      const average = run.roster.reduce((s, c) => s + c.level, 0) / Math.max(1, run.roster.length)
      const level = Math.max(1, Math.round(average))
      // Stats are rolled now, so the cards show exactly what would join.
      // Recruits draw rarity-weighted from the WHOLE catalog (v2.75) —
      // they are an in-run reward like a Wilds card, not a purchase.
      const options = rollRecruitOptions(3, [...taken], run).map(id => ({
        defId: id,
        stats: rollRecruitStats(getCreatureDef(id).stats, level - 1),
      }))
      return { type: 'recruit', options, level }
    },
  },
  {
    id: 'fledgling-companion',
    name: 'Fledgling Companion',
    icon: '🐣',
    description: 'Increase max roster size by 1. Choose 1 of 3 young creatures — they join at HALF your average level.',
    pool: 'regular',
    tags: ['creature', 'roster', 'regular'],
    weight: 30,
    simulation: sim,
    canOffer: run =>
      !hasWeeklyMod(run, 'loadout')
      && run.roster.length < TUNING.rewards.maxRosterSize
      && CREATURE_POOL.some(c => !run.roster.some(r => r.defId === c.id)),
    apply: (run) => {
      run.maxRosterSize++
      const taken = new Set(run.roster.map(c => c.defId))
      const average = run.roster.reduce((s, c) => s + c.level, 0) / Math.max(1, run.roster.length)
      // Rounds DOWN (v0.78): fledglings err on the young side.
      const level = Math.max(1, Math.floor(average / 2))
      const options = rollRecruitOptions(3, [...taken], run).map(id => ({
        defId: id,
        stats: rollRecruitStats(getCreatureDef(id).stats, level - 1),
      }))
      return { type: 'recruit', options, level }
    },
  },
  {
    // 🎓 Training Ground (v0.84, per Javi): the Training-Grounds pick as
    // an INSTANT battle reward — 1 of 3 skills, then choose the student.
    // Sims can't value skills (bots never use them) → STATIC_PIN Epic.
    id: 'training-ground',
    name: 'Training Ground',
    icon: '🎓',
    description: 'Pick 1 of 3 trained skills, then choose a creature — it learns the skill instantly.',
    pool: 'regular',
    tags: ['skill', 'regular'],
    weight: 10,
    simulation: sim,
    // Offerable while any skill is still unknown to the WHOLE roster —
    // and only from Ascension 1, the same gate the Training Grounds
    // MISSION has carried since v2.30. v3.81 (Javi: "in normal mode,
    // trained skills are not supposed to appear as a reward option"):
    // this card was the other door into the same content and nobody put
    // a lock on it, so Ascension 0 met trained skills here anyway.
    canOffer: run => (run.ascension ?? 0) >= 1 && teachableSkills(run).length > 0,
    apply: (run) => {
      // v5.17: unified through rollTrainingOptions — synergy nudge and
      // decline cycle included, same as the field school.
      const options = rollTrainingOptions(run)
      return { type: 'train', options, chosen: null }
    },
  },

  // ---- HEALING_POOL: rest nodes only ----
  {
    id: 'rest-stop',
    name: 'Rest Stop',
    icon: '🛏️',
    description: 'Heal 15 HP.',
    pool: 'healing',
    tags: ['healing', 'hp', 'rest'],
    weight: 80,
    simulation: sim,
    canOffer: run => run.hp < run.maxHp,
    apply: run => heal(run, 15),
  },
  // Campfire Meal removed in v0.54 (per Javi): +15 max HP compounded too
  // hard with the raised HP economy. normalizeRun strips stale offers.
  {
    id: 'full-camp',
    name: 'Full Camp',
    icon: '⛺',
    description: 'Heal 50% of your max HP.',
    describe: run => `Heal 50% of your max HP (${Math.ceil(run.maxHp * 0.5)} HP).`,
    pool: 'healing',
    tags: ['healing', 'hp', 'scaling', 'rest'],
    weight: 70,
    simulation: sim,
    canOffer: run => run.hp < run.maxHp,
    apply: run => heal(run, Math.ceil(run.maxHp * 0.5)),
  },
  {
    id: 'deep-rest',
    name: 'Deep Rest',
    icon: '🌙',
    description: 'Heal all of your HP.',
    describe: run => `Heal all of your HP (${run.maxHp - run.hp} HP).`,
    pool: 'healing',
    tags: ['healing', 'hp', 'full_heal', 'rest'],
    weight: 40,
    simulation: sim,
    canOffer: run => run.hp < run.maxHp,
    apply: (run) => {
      run.hp = run.maxHp
    },
  },
  {
    id: 'hardy-rest',
    name: 'Hardy Rest',
    icon: '❤️‍🔥',
    description: 'Heal 5 HP. Gain +5 max HP.',
    pool: 'healing',
    tags: ['healing', 'hp', 'max_hp', 'rest'],
    weight: 70,
    simulation: sim,
    apply: (run) => {
      run.maxHp += 5
      heal(run, 5)
    },
  },
  // Ascension cures (v0.60): injuries only exist on Ascension 1+, and on
  // The tiered field medicine (v5.22): each cure clears WHOLE creatures.
  {
    id: 'field-dressing',
    needsInjury: true,
    name: 'Field Dressing',
    icon: '🩹',
    description: 'Heal 5 HP. Fully cure 1 injured creature.',
    pool: 'healing',
    tags: ['healing', 'injury', 'hp', 'cleanse', 'rest'],
    weight: 80,
    simulation: sim,
    canOffer: anyInjured,
    apply: (run) => {
      heal(run, 5)
      cureCreatures(run, 1)
    },
  },
  {
    id: 'trail-medkit',
    needsInjury: true,
    name: 'Trail Medkit',
    icon: '🧰',
    description: 'Heal 5 HP. Fully cure 2 injured creatures.',
    pool: 'healing',
    tags: ['healing', 'injury', 'hp', 'cleanse', 'rest'],
    weight: 65,
    simulation: sim,
    canOffer: anyInjured,
    apply: (run) => {
      heal(run, 5)
      cureCreatures(run, 2)
    },
  },
  {
    id: 'miracle-salve',
    needsInjury: true,
    name: 'Miracle Salve',
    icon: '✨',
    description: 'Heal 5 HP. Cure ALL injuries on every creature.',
    pool: 'healing',
    tags: ['healing', 'injury', 'hp', 'cleanse', 'rest'],
    weight: 50,
    simulation: sim,
    canOffer: anyInjured,
    apply: (run) => {
      heal(run, 5)
      cleanseInjuries(run)
    },
  },
  {
    id: 'fortifying-meal',
    name: 'Fortifying Meal',
    icon: '🍖',
    description: 'Heal 10 HP. Gain +10 max HP.',
    pool: 'healing',
    tags: ['healing', 'hp', 'max_hp', 'rest'],
    weight: 70,
    simulation: sim,
    apply: (run) => {
      run.maxHp += 10
      heal(run, 10)
    },
  },
]


/* 🎨 Painted reward icons (v1.90, per Javi): keyed by reward id →
 * filename in public/ui/rewards. Missing entries fall back to emoji. */
const REWARD_IMAGES: Record<string, string> = {
  'group-practice': 'grouppractice',
  'field-promotion': 'fieldpromotion',
  'private-lesson': 'privatelesson',
  'catch-up-training': 'catchuptraining',
  'shared-notes': 'sharednotes',
  'battle-review': 'battlereview',
  'team-drill': 'teamdrill',
  'new-companion': 'newcompanion',
  'fledgling-companion': 'fledgingcompanion',
  'training-ground': 'trainingground',
  'rest-stop': 'reststop',
  'full-camp': 'fullcamp',
  'deep-rest': 'deeprest',
  'hardy-rest': 'hardyrest',
  'field-dressing': 'fielddressing',
  'trail-medkit': 'trailmedkit',
  'miracle-salve': 'miraclesalve',
  'fortifying-meal': 'fortifyingmeal',
}

for (const def of REWARD_DEFS) {
  const file = REWARD_IMAGES[def.id]
  if (file) def.image = `/ui/rewards/${file}.webp`
}

export function getRewardDef(id: string): RewardDef {
  const found = REWARD_DEFS.find(r => r.id === id)
  if (!found) throw new Error(`Unknown reward def: ${id}`)
  return found
}
